import React, { useMemo } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useTranslation } from "next-i18next";
import { Tag } from "@/types/cloud";
import { selectStyles } from "@/utils/selectStyles";
import FileTag from "@/components/common/FileTag";

const animatedComponents = makeAnimated();

const SelectAdvanced = ({
  readOnly = false,
  lg = false,
  options = [],
  value,
  isInvalid = false,
  sm = false,
  rounded = true,
  ...rest
}: {
  readOnly?: boolean;
  lg?: boolean;
  options: Tag[];
  value: number | number[];
  isInvalid?: boolean;
  sm?: boolean;
  rounded?: boolean;
  [x: string]: unknown;
}): React.ReactElement => {
  const { t } = useTranslation("common");

  const tagsOptions = useMemo(
    () => options?.map(({ id, name }) => ({ label: name, value: id })),
    [options]
  );

  const tagsByKeys = useMemo(
    () =>
      options?.reduce(
        (readyTags, tag) => ({
          ...readyTags,
          [tag.id]: tag,
        }),
        {}
      ),
    [options]
  );

  const tagValue = useMemo(
    () =>
      Array.isArray(value)
        ? value?.map((tagId) => tagsOptions.find((c) => tagId == c.value))
        : tagsOptions.find((c) => value == c.value),
    [tagsOptions, value]
  );

  const Option = (props: { data: { label: string; value: number } }) => {
    const { data, ...rest } = props;
    const tag: Tag = tagsByKeys[data.value];

    return (
      <animatedComponents.Option {...rest}>
        <FileTag
          name={tag.name}
          colorScheme={tag?.tagGroup?.colorScheme}
          picture={tag?.picture}
        />
      </animatedComponents.Option>
    );
  };

  const formatOptionLabel = ({ label, value }) => {
    const tag: Tag = tagsByKeys[value];

    return (
      <FileTag
        key={value}
        name={label}
        colorScheme={tag?.tagGroup?.colorScheme}
        picture={tag?.picture}
      />
    );
  };

  return (
    <Select
      isMulti={Array.isArray(value)}
      styles={selectStyles(lg, isInvalid, true, sm, rounded)}
      placeholder={t("choose")}
      isSearchable={!readOnly}
      menuIsOpen={readOnly ? false : undefined}
      noOptionsMessage={() => t("noOptions")}
      components={{ Option }}
      options={tagsOptions}
      formatOptionLabel={formatOptionLabel}
      value={tagValue}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      {...rest}
    />
  );
};

export default SelectAdvanced;
